import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FiltersDropdown from 'feature/dispatching/components/FiltersDropdown';
import { useFilterCount, useUserRolePermissions } from 'hooks';
import { FilterCountChip } from 'feature/dispatching/components/FilterChips';

const LocationControls = ({
  cardsFilters,
  defaultFiltersState,
  isLoading,
  isSuccess,
  onCreateTrailer,
  resetFilters,
  setCardsFilters,
}) => {
  const dropdownAnchorRef = useRef();
  const [filtersOpened, setFiltersOpen] = useState(false);
  const { isDispatchingManage } = useUserRolePermissions();

  const openFilters = R.compose(setFiltersOpen, R.T);
  const closeFilters = R.compose(setFiltersOpen, R.F);

  const handleCreateTrailer = () => {
    onCreateTrailer();
  };

  // prettier-ignore
  const handleFilterChange = filter => R.compose(
    setCardsFilters,
    R.assoc(filter),
    R.path(['target', 'value'])
  );

  const { filterCount } = useFilterCount(cardsFilters, defaultFiltersState);

  // prettier-ignore
  const filtersIcon = filtersOpened
    ? <ExpandLessRoundedIcon position="right" variant="buttonsChild" />
    : <ExpandMoreRoundedIcon position="right" variant="buttonsChild" />

  return (
    <>
      <Box>
        <Button
          disabled={!isSuccess}
          onClick={openFilters}
          onClose={closeFilters}
          ref={dropdownAnchorRef}
          sx={{ ml: 3 }}
          variant="text"
        >
          Filters
          {!!filterCount && <FilterCountChip count={filterCount} />}
          {filtersIcon}
        </Button>

        {isDispatchingManage && (
          <Button
            disabled={isLoading}
            onClick={handleCreateTrailer}
            sx={{ ml: 3 }}
            variant="contained"
          >
            New Trailer
          </Button>
        )}
      </Box>
      <FiltersDropdown
        anchorEl={dropdownAnchorRef.current}
        onClose={closeFilters}
        open={filtersOpened}
      >
        <TextField
          label="Trailer"
          name="trailer"
          onChange={handleFilterChange('trailer')}
          value={cardsFilters.trailer}
        />

        <FormControl fullWidth sx={{ mt: 3 }}>
          <InputLabel id="exp-filter-label">Expiration</InputLabel>
          <Select
            label="Expiration"
            labelId="exp-filter-label"
            onChange={handleFilterChange('expiration')}
            value={cardsFilters.expiration}
          >
            <MenuItem value="Any">Any</MenuItem>
            <MenuItem value="Valid">Valid</MenuItem>
            <MenuItem value="Expired">Expired</MenuItem>
            <MenuItem value="30 Days">Expires within 30 days</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mt: 3 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            label="Status"
            labelId="status-filter-label"
            onChange={handleFilterChange('status')}
            value={cardsFilters.status}
          >
            <MenuItem value="Any">Any</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
            <MenuItem value="GRACE">Grace Period</MenuItem>
            <MenuItem value="DEACTIVATED">Deactivated</MenuItem>
            <MenuItem value="WARNING">Warning</MenuItem>
          </Select>
        </FormControl>
        {Boolean(cardsFilters.expiration || cardsFilters.trailer || cardsFilters.status) && (
          <Button onClick={resetFilters} sx={{ mt: 2 }} variant="text">
            Clear
          </Button>
        )}
      </FiltersDropdown>
    </>
  );
};

LocationControls.propTypes = {
  cardsFilters: PropTypes.shape({
    expiration: PropTypes.string,
    status: PropTypes.string,
    trailer: PropTypes.string,
  }).isRequired,
  defaultFiltersState: PropTypes.shape({}).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSuccess: PropTypes.bool.isRequired,
  onCreateTrailer: PropTypes.func.isRequired,
  resetFilters: PropTypes.func.isRequired,
  setCardsFilters: PropTypes.func.isRequired,
};

export default LocationControls;
